import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Search,
  Send,
  X,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  ArrowRight,
  Globe,
  Rocket,
} from "lucide-react";
import { Link } from "@tanstack/react-router";
import {
  distributionsApi,
  keysApi,
  suppliersApi,
  relaySitesApi,
  mainTemplatesApi,
  pushSitesApi,
  templateRelaySitesApi,
  templatePushSitesApi,
  type ProtocolType,
  type DistributionTask,
  type TaskStatus,
  type MainTemplate,
  ApiError,
  RequestTimeoutError,
} from "@/api/client";
import { Badge } from "@/components/ui/badge";
import { ModelMappingEditor } from "@/components/ModelMappingEditor";
import { withSharedDisplayName } from "@/lib/keyNames";
import { mapWithConcurrency } from "@/lib/boundedConcurrency";
import { fieldMappingOverrides, modelSourceLabel, resolveModelConfigLayers } from "@/lib/modelConfig";
import { TargetConfigHint, type TargetConfigHintData } from "@/components/TargetConfigHint";
import { ADAPTER_PROFILES, missingBindingKey, requiredBindingKeysFor } from "@/adapters/pushProfiles";

// ---------------------------------------------------------------------------
// /distribute — 主模板 · 一键分发(005 分发合并单页;ADR-2/4/5)
//
// 旧的 /relay/distribute + /push/distribute 两页合并为单一入口:运营选一份主模板,
// 页面把该模板已绑定的中转站 + 推送站预勾选(可临时增减),录入 Key 后一次提交,
// 后端按 (site_type, code) 混合批同时下发到两类站点。
//
// 站点身份用「表内唯一 code」的复合键 (site_type, code):relay_site.code 与
// push_site.code 各自独立唯一,故选中态拆成 selectedRelay / selectedPush 两份
// Set<code>,提交时直接拆成 relay_site_codes / push_site_codes。裸 site_id 会跨表
// 撞号,不能作跨类身份。
// ---------------------------------------------------------------------------

const OFFLINE_FALLBACK_PROTOCOLS: ProtocolType[] = [
  { name: "OpenAI", type: 1 },
  { name: "Aws", type: 33 },
];

// 分发页只消费站点子集字段。code 是复合键的一半,必带。中转站与推送站共用。
// doc/011:推送站无站点级 api_contract;必填 key 判定在渲染时经
// pushFieldMappingBySite(由 template_push_site.field_mapping 汇总)做。
interface DistSiteLite {
  id: number;
  code: string;
  name: string;
  url: string;
  adapter_type: string;
  enabled: boolean;
  models: string | null;
  model_mapping: string | null;
  site_options?: Record<string, string>;
}

function isModelMappingValueUsable(s: string): boolean {
  const v = s.trim();
  if (v === "" || v === "{}" || v === "null") return true;
  try {
    const parsed = JSON.parse(v);
    return parsed !== null && typeof parsed === "object" && !Array.isArray(parsed);
  } catch {
    return false;
  }
}

function splitModels(csv: string): string[] {
  return Array.from(new Set(csv.split(",").map((m) => m.trim()).filter(Boolean)));
}

function isTaskSettled(status: TaskStatus): boolean {
  return status === "success" || status === "failed" || status === "unknown" || status === "abandoned";
}

const statusVariant: Record<TaskStatus, "default" | "success" | "warning" | "destructive" | "muted"> = {
  staged: "muted",
  pending: "muted",
  dispatching: "warning",
  success: "success",
  failed: "destructive",
  unknown: "warning",
  abandoned: "muted",
};

const STATUS_LABEL: Record<TaskStatus, string> = {
  staged: "暂存",
  pending: "待处理",
  dispatching: "分发中",
  success: "成功",
  failed: "失败",
  unknown: "未知",
  abandoned: "已放弃",
};

function relativeTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "—";
  const diff = Date.now() - then;
  if (diff < 0) return "刚刚";
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s} 秒前`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} 分钟前`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} 小时前`;
  return `${Math.floor(h / 24)} 天前`;
}

const sectionCls = "rounded-xl border bg-card shadow-sm";
const inpCls = "h-9 w-full rounded-md border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring";
const labelCls = "text-xs font-medium text-foreground";
const hintCls = "text-[11px] text-muted-foreground";

type Mode = "single" | "multi" | "batch";

const KEY_SUBTITLE: Record<Mode, string> = {
  single: "单行输入框,仅允许填写 1 个密钥;明文仅用于本次提交,落库前 AES-256-GCM 加密。",
  multi: "大文本域,每行一个密钥;右上角可一键去重。多把密钥各生成一条上游 Key 记录。",
  batch: "大文本域,每行一个密钥;每一行会各生成一条上游 Key 记录并分发到所选站点。",
};
const KEY_HINT: Record<Mode, string | null> = {
  single: null,
  multi: "多密钥:每行一个密钥,系统为每行生成一条上游 Key,再逐条分发到所选站点。",
  batch: "批量添加:每行一个密钥,每一行在下游各站生成一条独立渠道记录。",
};

// 密钥格式 —— 仅前端呈现层(后端 APIKey 无此字段):驱动 Key 输入框 placeholder,
// 并对 SigV4(AK|SK|Region 管道分隔)做轻校验。参考 demo-template-distribute-A-wizard.html。
type KeyFormat = "bearer" | "x-api-key" | "sigv4";
const KEY_FORMAT_LABEL: Record<KeyFormat, string> = {
  bearer: "Bearer (Authorization: Bearer <key>)",
  "x-api-key": "x-api-key (header X-Api-Key: <key>)",
  sigv4: "AK|SK|Region (SigV4)",
};
const KEY_FORMAT_PLACEHOLDER: Record<KeyFormat, { single: string; multi: string }> = {
  bearer: { single: "sk-ant-...", multi: "sk-ant-...\nsk-ant-...\nsk-ant-..." },
  "x-api-key": { single: "sk-...", multi: "sk-...\nsk-...\nsk-..." },
  sigv4: { single: "AKIA...|SECRET...|us-east-1", multi: "AKIA...|SECRET...|us-east-1\nAKIA...|SECRET...|eu-west-1" },
};
const SIGV4_RE = /^[^\s|]+\|[^\s|]+\|[^\s|]+$/;
const DISTRIBUTION_SUBMIT_CONCURRENCY = 2;
const DISTRIBUTION_CREATE_TIMEOUT_MS = 60_000;
// 前台轮询护栏:超过此时长仍未全部定局则停止转圈,提示已转后台(任务仍在后端异步推进)。
const LIVE_WATCH_MAX_MS = 5 * 60_000;

function fmtDuration(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${s % 60}s`;
}

function chunksOf<T>(items: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) chunks.push(items.slice(i, i + size));
  return chunks;
}

function newDistributionOperationId(): string {
  if (typeof globalThis.crypto?.randomUUID === "function") {
    return `bizdist:${globalThis.crypto.randomUUID()}`;
  }
  // randomUUID requires a secure browser context. The identifier is not a
  // credential, so an entropy-rich compatibility fallback is sufficient for
  // internal HTTP deployments; the backend still validates its namespace.
  return `bizdist:${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
}

async function confirmCreatedDistribution(
  operationId: string,
  keyId: number,
  signal: AbortSignal,
): Promise<DistributionTask[]> {
  const result = await distributionsApi.list({
    operation_id: operationId,
    key_id: keyId,
    page: 1,
    page_size: 100,
    signal,
  });
  return result.items;
}

export function DistributePage() {
  const qc = useQueryClient();

  const fetchRelaySites = useCallback((): Promise<DistSiteLite[]> =>
    relaySitesApi.list().then((r) => r.items.map((s) => ({
      id: s.id, code: s.code, name: s.name, url: s.url, adapter_type: s.adapter_type, enabled: s.enabled,
      models: s.models, model_mapping: s.model_mapping,
    }))), []);
  const fetchPushSites = useCallback((): Promise<DistSiteLite[]> =>
    pushSitesApi.list().then((r) => r.items.map((s) => ({
      id: s.id, code: s.code, name: s.name, url: s.url, adapter_type: s.adapter_type, enabled: s.enabled,
      models: s.models, model_mapping: s.model_mapping, site_options: s.site_options,
    }))), []);

  const relaySitesQ = useQuery({ queryKey: ["distribute-relay-sites"], queryFn: fetchRelaySites });
  const pushSitesQ = useQuery({ queryKey: ["distribute-push-sites"], queryFn: fetchPushSites });
  const keyCountQ = useQuery({
    queryKey: ["keys", "count"],
    queryFn: () => keysApi.list({ page: 1, page_size: 1 }),
  });
  const suppliersQ = useQuery({ queryKey: ["suppliers"], queryFn: suppliersApi.list });
  const templatesQ = useQuery({ queryKey: ["distribute-main-templates", "enabled"], queryFn: () => mainTemplatesApi.list({ enabled: true }), staleTime: 30_000 });
  const relayBindsQ = useQuery({ queryKey: ["template-relay-sites"], queryFn: () => templateRelaySitesApi.listAll(), staleTime: 30_000 });
  const pushBindsQ = useQuery({ queryKey: ["template-push-sites"], queryFn: () => templatePushSitesApi.listAll(), staleTime: 30_000 });

  const protocols: ProtocolType[] =
    suppliersQ.data?.items ?? (suppliersQ.isError ? OFFLINE_FALLBACK_PROTOCOLS : []);
  const protoName = useMemo(() => {
    const m = new Map<number, string>();
    protocols.forEach((p) => m.set(p.type, p.name));
    return m;
  }, [protocols]);

  const tasksQ = useQuery({
    queryKey: ["tasks", "recent"],
    queryFn: () => distributionsApi.list({ latest_operation: true, page: 1, page_size: 100 }),
  });
  const recentKeyIDs = useMemo(
    () => Array.from(new Set((tasksQ.data?.items ?? []).map((task) => task.key_id))),
    [tasksQ.data],
  );
  const recentKeysQ = useQuery({
    queryKey: ["keys", "recent-tasks", recentKeyIDs],
    queryFn: () => keysApi.list({ ids: recentKeyIDs, page: 1, page_size: Math.max(1, recentKeyIDs.length) }),
    enabled: recentKeyIDs.length > 0,
  });

  const [mode, setMode] = useState<Mode>("single");
  // 选中态:两份按 code 索引的集合,与两列一一对应(见文件头注释)。
  const [selectedRelay, setSelectedRelay] = useState<Set<string>>(new Set());
  const [selectedPush, setSelectedPush] = useState<Set<string>>(new Set());
  const [relaySearch, setRelaySearch] = useState("");
  const [pushSearch, setPushSearch] = useState("");
  const keyInputRef = useRef<HTMLInputElement | null>(null);
  const multiInputRef = useRef<HTMLTextAreaElement | null>(null);
  const batchInputRef = useRef<HTMLTextAreaElement | null>(null);
  function changeMode(next: Mode) {
    if (next === mode) return;
    setKeyText("");
    setMultiText("");
    setBatchText("");
    setMode(next);
  }
  useEffect(() => {
    if (mode === "single") keyInputRef.current?.focus();
    else if (mode === "multi") multiInputRef.current?.focus();
    else batchInputRef.current?.focus();
  }, [mode]);

  const [activeTemplateId, setActiveTemplateId] = useState<number | null>(null);

  const [name, setName] = useState("");
  const [protocolType, setProtocolType] = useState<number | null>(null);
  const [keyFormat, setKeyFormat] = useState<KeyFormat>("bearer");
  const [keyText, setKeyText] = useState("");
  const [baseUrl, setBaseUrl] = useState("");
  const [models, setModels] = useState("");
  const [modelMapping, setModelMapping] = useState("");
  const [advOpen, setAdvOpen] = useState(false);
  const [priorityInput, setPriorityInput] = useState("");
  const [supplierField, setSupplierField] = useState("");
  const [unitPrice, setUnitPrice] = useState("");

  const [batchText, setBatchText] = useState("");
  const [multiText, setMultiText] = useState("");

  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [batchErrors, setBatchErrors] = useState<
    { index: number; name: string; error: string; kind: "import" | "dispatch" | "confirm" }[]
  >([]);
  const submitAbortRef = useRef<AbortController | null>(null);

  type LiveEntry = { taskId: number; keyName: string; siteName: string; status: TaskStatus; error?: string };
  const [liveWatch, setLiveWatch] = useState<{ ids: number[]; startedAt: number } | null>(null);
  const [liveResult, setLiveResult] = useState<LiveEntry[] | null>(null);
  const [liveSettled, setLiveSettled] = useState(0);
  // 在途明细快照 / 轮询瞬时错误 / 已达最大前台等待时长（护栏，防无限转圈）/ 已等待毫秒。
  const [liveEntries, setLiveEntries] = useState<LiveEntry[]>([]);
  const [liveError, setLiveError] = useState<string | null>(null);
  const [liveStalled, setLiveStalled] = useState(false);
  const [liveElapsed, setLiveElapsed] = useState(0);

  const enabledRelay = useMemo<DistSiteLite[]>(
    () => relaySitesQ.data?.filter((s) => s.enabled) ?? [],
    [relaySitesQ.data],
  );
  const enabledPush = useMemo<DistSiteLite[]>(
    () => pushSitesQ.data?.filter((s) => s.enabled) ?? [],
    [pushSitesQ.data],
  );
  const batchKeyLines = useMemo(() => batchText.split("\n").map((s) => s.trim()).filter(Boolean), [batchText]);
  const multiKeyLines = useMemo(
    () => Array.from(new Set(multiText.split("\n").map((s) => s.trim()).filter(Boolean))),
    [multiText],
  );

  const keyById = useMemo(() => {
    const m = new Map<number, string>();
    recentKeysQ.data?.items.forEach((k) => m.set(k.id, k.name));
    return m;
  }, [recentKeysQ.data]);
  // 站点名解析走复合键 (site_type, site_code) —— 裸 id 会跨 relay/push 撞号。
  const relayNameByCode = useMemo(() => {
    const m = new Map<string, string>();
    relaySitesQ.data?.forEach((s) => m.set(s.code, s.name));
    return m;
  }, [relaySitesQ.data]);
  const pushNameByCode = useMemo(() => {
    const m = new Map<string, string>();
    pushSitesQ.data?.forEach((s) => m.set(s.code, s.name));
    return m;
  }, [pushSitesQ.data]);
  const siteLabel = useCallback((t: DistributionTask): string => {
    const map = t.site_type === "push" ? pushNameByCode : relayNameByCode;
    return map.get(t.site_code) ?? (t.site_code || `#${t.site_id}`);
  }, [relayNameByCode, pushNameByCode]);
  // 绑定预选需要 site_id → code(binding 汇总接口只带 site_id)。
  const relayCodeById = useMemo(() => {
    const m = new Map<number, DistSiteLite>();
    relaySitesQ.data?.forEach((s) => m.set(s.id, s));
    return m;
  }, [relaySitesQ.data]);
  const pushCodeById = useMemo(() => {
    const m = new Map<number, DistSiteLite>();
    pushSitesQ.data?.forEach((s) => m.set(s.id, s));
    return m;
  }, [pushSitesQ.data]);

  // site_id → 该推送站全部绑定行的 field_mapping(可能有主模板×多对多的多条)。
  // 分发警示判定「是否存在某条绑定行的 field_mapping 满足该商家必填 key」。
  const pushFieldMappingBySite = useMemo(() => {
    const m = new Map<number, string[]>();
    (pushBindsQ.data?.items ?? []).forEach((b) => {
      const arr = m.get(b.site_id) ?? [];
      arr.push(b.field_mapping ?? "");
      m.set(b.site_id, arr);
    });
    return m;
  }, [pushBindsQ.data]);

  // 选中站点里是否有人要求聚合上传:站点默认 upload_mode=aggregate,或
  // 当前主模板的绑定 field_mapping 覆盖为 aggregate。命中时整批先 staged,
  // 等所有 Key 都创建完再 Commit,聚合组才是完整的。
  const aggregateSelected = useMemo(() => {
    const codeToSite = new Map<string, DistSiteLite>();
    (pushSitesQ.data ?? []).forEach((s) => codeToSite.set(s.code, s));
    for (const code of selectedPush) {
      const site = codeToSite.get(code);
      if (!site) continue;
      if (site.site_options?.upload_mode === "aggregate") return true;
      for (const b of pushBindsQ.data?.items ?? []) {
        if (b.site_id !== site.id) continue;
        if (activeTemplateId != null && b.template_id !== activeTemplateId) continue;
        try {
          const entries = JSON.parse(b.field_mapping || "[]") as { key?: string; value?: string }[];
          if (entries.some((e) => e.key === "upload_mode" && e.value === "aggregate")) return true;
        } catch {
          // 非法 JSON 由后端 pre-flight 拒绝,这里不据此判断聚合。
        }
      }
    }
    return false;
  }, [selectedPush, pushSitesQ.data, pushBindsQ.data, activeTemplateId]);

  // 该站「缺配置」= 商家有必填绑定 key,但没有任何一条绑定行的 field_mapping 满足。
  function pushSiteMissingKey(s: DistSiteLite): string | null {
    const required = requiredBindingKeysFor(s.adapter_type);
    if (required.length === 0) return null;
    const mappings = pushFieldMappingBySite.get(s.id) ?? [];
    for (const fm of mappings) {
      if (missingBindingKey(s.adapter_type, fm) === null) return null;
    }
    return required[0];
  }

  // 选中主模板后,下发目标只展示「绑定到该模板」的站点;未选模板(手动)= 全部启用站点。
  // null = 不过滤;非 null = 只保留 set 内的 code。
  const boundRelayCodes = useMemo(() => {
    if (activeTemplateId == null) return null;
    const s = new Set<string>();
    (relayBindsQ.data?.items ?? [])
      .filter((b) => b.template_id === activeTemplateId)
      .forEach((b) => { const site = relayCodeById.get(b.site_id); if (site) s.add(site.code); });
    return s;
  }, [activeTemplateId, relayBindsQ.data, relayCodeById]);
  const boundPushCodes = useMemo(() => {
    if (activeTemplateId == null) return null;
    const s = new Set<string>();
    (pushBindsQ.data?.items ?? [])
      .filter((b) => b.template_id === activeTemplateId)
      .forEach((b) => { const site = pushCodeById.get(b.site_id); if (site) s.add(site.code); });
    return s;
  }, [activeTemplateId, pushBindsQ.data, pushCodeById]);

  // 可选池:选模板 → 仅其绑定的启用站点;否则全部启用站点。计数分母与「全选」都基于池。
  const relayPool = useMemo(
    () => (boundRelayCodes ? enabledRelay.filter((s) => boundRelayCodes.has(s.code)) : enabledRelay),
    [enabledRelay, boundRelayCodes],
  );
  const pushPool = useMemo(
    () => (boundPushCodes ? enabledPush.filter((s) => boundPushCodes.has(s.code)) : enabledPush),
    [enabledPush, boundPushCodes],
  );

  const filteredRelay = useMemo(() => {
    const q = relaySearch.trim().toLowerCase();
    if (!q) return relayPool;
    return relayPool.filter((s) => s.name.toLowerCase().includes(q) || s.code.toLowerCase().includes(q) || s.url.toLowerCase().includes(q));
  }, [relayPool, relaySearch]);
  const filteredPush = useMemo(() => {
    const q = pushSearch.trim().toLowerCase();
    if (!q) return pushPool;
    return pushPool.filter((s) => s.name.toLowerCase().includes(q) || s.code.toLowerCase().includes(q) || s.url.toLowerCase().includes(q));
  }, [pushPool, pushSearch]);

  function toggleRelay(code: string) {
    setSelectedRelay((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code); else next.add(code);
      return next;
    });
  }
  function togglePush(code: string) {
    setSelectedPush((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code); else next.add(code);
      return next;
    });
  }
  function selectAllRelay(on: boolean) {
    setSelectedRelay(on ? new Set(relayPool.map((s) => s.code)) : new Set());
  }
  function selectAllPush(on: boolean) {
    setSelectedPush(on ? new Set(pushPool.map((s) => s.code)) : new Set());
  }

  const templates: MainTemplate[] = templatesQ.data?.items ?? [];
  const activeTemplate = useMemo(
    () => templates.find((t) => t.id === activeTemplateId) ?? null,
    [templates, activeTemplateId],
  );
  const templateModels = activeTemplate?.models ?? "";
  const modelOptions = useMemo(
    () => splitModels(models.trim() !== "" ? models : templateModels),
    [models, templateModels],
  );

  type HintTarget = { kind: "relay" | "push"; site: DistSiteLite; rect: DOMRect };
  const [hintTarget, setHintTarget] = useState<HintTarget | null>(null);
  const hideHintTimerRef = useRef<number | null>(null);
  function cancelHideTargetHint() {
    if (hideHintTimerRef.current === null) return;
    window.clearTimeout(hideHintTimerRef.current);
    hideHintTimerRef.current = null;
  }
  function showTargetHint(kind: "relay" | "push", site: DistSiteLite, el: HTMLElement) {
    cancelHideTargetHint();
    setHintTarget({ kind, site, rect: el.getBoundingClientRect() });
  }
  function hideTargetHint() {
    cancelHideTargetHint();
    setHintTarget(null);
  }
  // tooltip 内容溢出、可滚动时，鼠标需要能移入 tooltip；离开目标后延迟收起。
  function scheduleHideTargetHint() {
    cancelHideTargetHint();
    hideHintTimerRef.current = window.setTimeout(() => {
      hideHintTimerRef.current = null;
      setHintTarget(null);
    }, 180);
  }
  useEffect(() => () => {
    if (hideHintTimerRef.current !== null) window.clearTimeout(hideHintTimerRef.current);
  }, []);

  const targetHint = useMemo<TargetConfigHintData | null>(() => {
    if (!hintTarget) return null;
    const { kind, site } = hintTarget;
    const relayBinding =
      activeTemplateId == null
        ? undefined
        : relayBindsQ.data?.items.find((b) => b.template_id === activeTemplateId && b.site_id === site.id);
    const pushBinding =
      activeTemplateId == null
        ? undefined
        : pushBindsQ.data?.items.find((b) => b.template_id === activeTemplateId && b.site_id === site.id);
    const binding = kind === "relay" ? relayBinding : pushBinding;
    const override = kind === "push" ? fieldMappingOverrides(pushBinding?.field_mapping) : {};
    const resolved = resolveModelConfigLayers({
      binding: binding ? { models: binding.models, modelMapping: binding.model_mapping } : undefined,
      site: { models: site.models, modelMapping: site.model_mapping },
      distribution: { models, modelMapping },
      template: activeTemplate
        ? { models: activeTemplate.models, modelMapping: activeTemplate.model_mapping }
        : undefined,
      pushFieldMapping: kind === "push" ? pushBinding?.field_mapping : undefined,
    });
    const mappingCount = (() => {
      if (kind !== "push" || !pushBinding?.field_mapping) return 0;
      try {
        const parsed = JSON.parse(pushBinding.field_mapping);
        return Array.isArray(parsed) ? parsed.length : 0;
      } catch {
        return 0;
      }
    })();
    const missingKey =
      kind === "push" && pushBinding
        ? missingBindingKey(site.adapter_type, pushBinding.field_mapping)
        : null;
    const rows =
      kind === "relay"
        ? [
            { label: "站点", value: `${site.name} (${site.code})` },
            { label: "适配器", value: site.adapter_type },
            { label: "URL", value: site.url },
            { label: "groups", value: relayBinding?.groups || "—" },
            { label: "priority", value: relayBinding ? String(relayBinding.priority) : "—" },
            { label: "proxy", value: relayBinding?.proxy || "—" },
          ]
        : [
            { label: "站点", value: `${site.name} (${site.code})` },
            { label: "适配器", value: site.adapter_type },
            { label: "URL", value: site.url },
            { label: "push_template", value: pushBinding ? `#${pushBinding.push_template_id}` : "—" },
            { label: "field_mapping", value: `${mappingCount} 个字段` },
            { label: "必填缺失", value: pushBinding ? missingKey || "无" : "未绑定模板" },
            { label: "upload_mode", value: site.site_options?.upload_mode || "—" },
            { label: "multi_key_mode", value: site.site_options?.multi_key_mode || "—" },
          ];
    const warning =
      activeTemplateId == null
        ? "未选主模板：绑定层未知，预览按站点 / 分发 / 模板估算。"
        : !binding
          ? "当前模板在该站点没有绑定行，绑定层按未设置处理。"
          : undefined;
    const overrideNote =
      kind === "push" && (override.models || override.modelMapping)
        ? "推送站 field_mapping 的 models/model_mapping 在四层解析后最终覆盖。"
        : undefined;
    return {
      title: `${kind === "relay" ? "中转站" : "推送站"} · ${site.name}`,
      subtitle: `${site.code} · ${site.adapter_type}`,
      rows,
      layers: [
        { label: "绑定", value: binding?.models ?? "" },
        { label: "站点", value: site.models ?? "" },
        { label: "分发", value: models },
        { label: "模板", value: activeTemplate?.models ?? "" },
      ],
      finalModels: resolved.models,
      finalModelsSource: modelSourceLabel(resolved.modelsSource),
      finalMapping: resolved.modelMapping,
      finalMappingSource: modelSourceLabel(resolved.modelMappingSource),
      warning,
      note: `留空（NULL / 空字符串）= 继承下一层。${overrideNote ?? ""}`,
    };
  }, [
    hintTarget,
    activeTemplateId,
    relayBindsQ.data,
    pushBindsQ.data,
    activeTemplate,
    models,
    modelMapping,
  ]);

  // 选主模板:预勾选其绑定的中转站 + 推送站(仅 enabled),并带出协议。
  // 模型/映射不自动写入分发层,避免模板值伪装成显式覆盖。
  function selectTemplate(id: number | null) {
    setActiveTemplateId(id);
    setModels("");
    setModelMapping("");
    if (id == null) return;
    const t = templates.find((x) => x.id === id);
    if (!t) return;

    const relayCodes = (relayBindsQ.data?.items ?? [])
      .filter((b) => b.template_id === id)
      .map((b) => relayCodeById.get(b.site_id))
      .filter((s): s is DistSiteLite => !!s && s.enabled)
      .map((s) => s.code);
    const pushCodes = (pushBindsQ.data?.items ?? [])
      .filter((b) => b.template_id === id)
      .map((b) => pushCodeById.get(b.site_id))
      .filter((s): s is DistSiteLite => !!s && s.enabled)
      .map((s) => s.code);
    setSelectedRelay(new Set(relayCodes));
    setSelectedPush(new Set(pushCodes));

    if (t.protocol_type != null && t.protocol_type > 0) setProtocolType(t.protocol_type);
  }

  function useTemplateDefaults() {
    if (!activeTemplate) return;
    setModels(activeTemplate.models ?? "");
    setModelMapping(activeTemplate.model_mapping ?? "");
  }

  function resetForm() {
    setName("");
    setKeyText("");
    setMultiText("");
    setBatchText("");
    setBaseUrl("");
    setModels("");
    setModelMapping("");
    setPriorityInput("");
    setSupplierField("");
    setUnitPrice("");
    setProtocolType(null);
    setActiveTemplateId(null);
    setSelectedRelay(new Set());
    setSelectedPush(new Set());
    setKeyFormat("bearer");
  }

  const totalSelected = selectedRelay.size + selectedPush.size;

  // 轻量提示:选了站点但没确定协议类型时,dispatcher 很可能 fail-fast。
  const unboundWarning = useMemo(() => {
    if (totalSelected === 0) return null;
    if (protocolType == null && activeTemplateId == null) {
      return `已选 ${totalSelected} 个站点但未选协议类型 / 主模板;若站点未绑对应协议模板,分发会失败`;
    }
    return null;
  }, [totalSelected, protocolType, activeTemplateId]);

  const submitMut = useMutation({
    mutationFn: async () => {
      const submitController = new AbortController();
      submitAbortRef.current = submitController;
      // One click is one operation, including every per-key request issued by
      // multi/batch mode. The backend uses this scope for the recent preview.
      const operationId = newDistributionOperationId();
      const targets = {
        relaySiteCodes: Array.from(selectedRelay),
        pushSiteCodes: Array.from(selectedPush),
        // 选了主模板时把 template_id 带给后端,push 任务会拿它去 template_push_site
        // 解析 push_template_id + field_mapping(ezlink /channels/batch 必需)。
        templateId: activeTemplateId ?? undefined,
      };
      const priority = priorityInput.trim() === "" ? undefined : Number(priorityInput);
      // 聚合站批次必须 staged:每个 key 的 create 只落库、不派发;整批完成后
      // 由 commit 统一放行,组长才能拿到全批 key 一次性合成渠道。单 Key 无需等待。
      const useStaged = aggregateSelected && mode !== "single";
      const keyBase = {
        name: "",
        key: "",
        supplier_field: supplierField.trim(),
        base_url: baseUrl.trim(),
        models: models.trim(),
        unit_price: unitPrice.trim() === "" ? 0 : Number(unitPrice),
        protocol_type: protocolType ?? undefined,
      };
      if (mode === "single") {
        const created = await keysApi.create({ ...keyBase, name: name.trim(), key: keyText });
        const mm = modelMapping.trim();
        try {
          const r = await distributionsApi.create(created.id, targets, {
            priority,
            model_mapping: mm !== "" ? mm : undefined,
            operation_id: operationId,
            staged: useStaged,
            signal: submitController.signal,
            timeoutMs: DISTRIBUTION_CREATE_TIMEOUT_MS,
          });
          return {
            kind: "single" as const,
            keyName: created.name,
            taskCount: r.tasks.length,
            taskIds: r.tasks.map((t) => t.id),
            confirmedAfterTimeout: false,
            pendingConfirmation: false,
          };
        } catch (error) {
          if (!(error instanceof RequestTimeoutError)) throw error;
          try {
            const tasks = await confirmCreatedDistribution(operationId, created.id, submitController.signal);
            if (tasks.length > 0) {
              return {
                kind: "single" as const,
                keyName: created.name,
                taskCount: tasks.length,
                taskIds: tasks.map((t) => t.id),
                confirmedAfterTimeout: true,
                pendingConfirmation: false,
              };
            }
          } catch {
            // A failed read-only confirmation must never trigger another POST.
          }
          return {
            kind: "single" as const,
            keyName: created.name,
            taskCount: 0,
            taskIds: [] as number[],
            confirmedAfterTimeout: false,
            pendingConfirmation: true,
          };
        }
      }
      const uniqueKeys = mode === "multi" ? multiKeyLines : batchKeyLines;
      if (uniqueKeys.length === 0) throw new Error("请输入至少一个密钥");
      const rows = withSharedDisplayName(uniqueKeys, name, name || "key").map(({ name: sharedName, key }) => ({
        ...keyBase,
        name: sharedName,
        key,
      }));
      const res = await keysApi.batchCreate(rows);
      const mm = modelMapping.trim();
      let dispatched = 0;
      let dispatchFailed = 0;
      let dispatchPending = 0;
      let timeoutConfirmed = 0;
      const dispatchErrors: { index: number; name: string; error: string; kind: "dispatch" | "confirm" }[] = [];
      const taskIds: number[] = [];
      const outcomes = await mapWithConcurrency(res.items, DISTRIBUTION_SUBMIT_CONCURRENCY, async (k, index) => {
        try {
          const result = await distributionsApi.create(k.id, targets, {
            priority,
            model_mapping: mm !== "" ? mm : undefined,
            operation_id: operationId,
            staged: aggregateSelected,
            signal: submitController.signal,
            timeoutMs: DISTRIBUTION_CREATE_TIMEOUT_MS,
          });
          return { status: "success" as const, index, keyName: k.name, tasks: result.tasks, confirmedAfterTimeout: false };
        } catch (error) {
          if (error instanceof RequestTimeoutError) {
            try {
              const tasks = await confirmCreatedDistribution(operationId, k.id, submitController.signal);
              if (tasks.length > 0) {
                return { status: "success" as const, index, keyName: k.name, tasks, confirmedAfterTimeout: true };
              }
            } catch {
              // Keep this Key uncertain; never repeat the mutating request.
            }
            return { status: "confirm" as const, index, keyName: k.name, error: error.message };
          }
          const message = error instanceof ApiError ? error.message : "分发失败";
          return { status: "failed" as const, index, keyName: k.name, error: message };
        }
      });
      if (useStaged && outcomes.some((o) => o.status === "success")) {
        await distributionsApi.commit(operationId);
      }
      for (const outcome of outcomes) {
        if (outcome.status === "success") {
          dispatched += outcome.tasks.length;
          taskIds.push(...outcome.tasks.map((task) => task.id));
          if (outcome.confirmedAfterTimeout) timeoutConfirmed += 1;
        } else if (outcome.status === "confirm") {
          dispatchPending += 1;
          dispatchErrors.push({ index: outcome.index, name: outcome.keyName, error: outcome.error, kind: "confirm" });
        } else {
          dispatchFailed += 1;
          dispatchErrors.push({ index: outcome.index, name: outcome.keyName, error: outcome.error, kind: "dispatch" });
        }
      }
      return {
        kind: mode,
        created: res.created,
        failed: res.failed,
        dispatched,
        dispatchFailed,
        dispatchPending,
        timeoutConfirmed,
        taskIds,
        errors: [
          ...(res.errors ?? []).map((e) => ({ index: e.index, name: e.name, error: e.error, kind: "import" as const })),
          ...dispatchErrors,
        ],
      };
    },
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["tasks"] });
      qc.invalidateQueries({ queryKey: ["keys"] });
      setErr(null);
      if ("taskIds" in r && r.taskIds.length > 0) {
        setLiveResult(null);
        setLiveSettled(0);
        setLiveEntries([]);
        setLiveError(null);
        setLiveStalled(false);
        setLiveElapsed(0);
        setLiveWatch({ ids: r.taskIds, startedAt: Date.now() });
      }
      if (r.kind === "single") {
        setBatchErrors([]);
        setMsg(r.pendingConfirmation
          ? `已创建 Key「${r.keyName}」；分发提交结果待确认，系统未重复发送`
          : `${r.confirmedAfterTimeout ? "请求超时后已确认：" : ""}已创建 Key「${r.keyName}」并分发到 ${r.taskCount} 个站点`);
        resetForm();
      } else {
        setBatchErrors(r.errors ?? []);
        const parts = [`导入成功 ${r.created} 条`];
        if (r.failed) parts.push(`导入失败 ${r.failed} 条`);
        parts.push(`分发 ${r.dispatched} 个任务行`);
        if (r.timeoutConfirmed) parts.push(`超时后已确认 ${r.timeoutConfirmed} 把`);
        if (r.dispatchFailed) parts.push(`分发失败 ${r.dispatchFailed} 个(详见下方明细)`);
        if (r.dispatchPending) parts.push(`提交结果待确认 ${r.dispatchPending} 个(未自动重发)`);
        setMsg(parts.join(" · "));
        if (r.failed === 0 && r.dispatchFailed === 0 && r.dispatchPending === 0) {
          if (r.kind === "multi") setMultiText("");
          else setBatchText("");
        }
      }
    },
    onError: (e) => {
      setMsg(null);
      const m = e instanceof RequestTimeoutError
        ? `${e.message}，系统不会自动重发，请稍后按 Key 查询确认`
        : e instanceof ApiError ? e.message : "提交失败";
      setErr(m);
    },
    onSettled: () => {
      submitAbortRef.current = null;
    },
  });

  useEffect(() => () => submitAbortRef.current?.abort(), []);

  const mappingUsable = isModelMappingValueUsable(modelMapping);
  // 当前模式下待提交的密钥行(单密钥 = 至多 1 行)。SigV4 需每行都是 AK|SK|Region。
  const keyLines = mode === "single" ? (keyText.trim() === "" ? [] : [keyText.trim()]) : mode === "multi" ? multiKeyLines : batchKeyLines;
  const sigv4Ok = keyFormat !== "sigv4" || (keyLines.length > 0 && keyLines.every((l) => SIGV4_RE.test(l)));
  const canSubmit =
    totalSelected > 0 &&
    !submitMut.isPending &&
    sigv4Ok &&
    (mode === "single"
      ? name.trim() !== "" && keyText !== "" && mappingUsable
      : mode === "multi"
        ? multiKeyLines.length > 0 && multiKeyLines.every((k) => k.trim() !== "")
        : batchKeyLines.length > 0 && batchKeyLines.every((k) => k.trim() !== ""));

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter" && canSubmit) {
        e.preventDefault();
        submitMut.mutate();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [canSubmit, submitMut]);

  useEffect(() => {
    if (!liveWatch) return;
    let cancelled = false;
    let done = false;
    let fails = 0;
    let timer: ReturnType<typeof setTimeout>;
    const controller = new AbortController();
    const poll = async () => {
      try {
        const items: DistributionTask[] = [];
        for (const ids of chunksOf(liveWatch.ids, 100)) {
          const list = await distributionsApi.list({ ids, page: 1, page_size: ids.length, signal: controller.signal });
          items.push(...list.items);
        }
        if (cancelled) return;
        const byId = new Map(items.map((t) => [t.id, t]));
        const entries = liveWatch.ids.map((id) => {
          const t = byId.get(id);
          return {
            taskId: id,
            keyName: keyById.get(t?.key_id ?? -1) ?? (t ? `#${t.key_id}` : "…"),
            siteName: t ? siteLabel(t) : "…",
            status: t?.status ?? "pending",
            error: t?.error_message ?? undefined,
          };
        });
        if (cancelled) return;
        fails = 0;
        setLiveError(null);
        setLiveEntries(entries);
        const settled = entries.filter((e) => isTaskSettled(e.status)).length;
        setLiveSettled(settled);
        if (settled === liveWatch.ids.length) {
          done = true;
          setLiveResult(entries);
          setLiveWatch(null);
          setLiveEntries([]);
          qc.invalidateQueries({ queryKey: ["tasks"] });
        }
      } catch {
        // 不再静默吞错:提示瞬时失败并继续重试,由下方最大时长护栏兜底。
        if (!cancelled) {
          fails += 1;
          setLiveError(fails <= 1 ? "刷新失败,正在自动重试…" : `连续 ${fails} 次刷新失败,仍在重试…`);
        }
      } finally {
        if (!cancelled && !done) {
          const elapsed = Date.now() - liveWatch.startedAt;
          setLiveElapsed(elapsed);
          if (elapsed > LIVE_WATCH_MAX_MS) {
            // 护栏触发:停止前台轮询,任务仍在后端异步推进,保留明细供查看。
            done = true;
            setLiveStalled(true);
          } else {
            timer = setTimeout(poll, 1500);
          }
        }
      }
    };
    poll();
    return () => {
      cancelled = true;
      controller.abort();
      clearTimeout(timer);
    };
  }, [liveWatch, keyById, siteLabel, qc]);

  function clearBanner() {
    setLiveWatch(null);
    setLiveResult(null);
    setLiveSettled(0);
    setLiveEntries([]);
    setLiveError(null);
    setLiveStalled(false);
    setLiveElapsed(0);
    setMsg(null);
    setErr(null);
  }

  // 重启前台轮询(护栏停止后的「继续等待」,或失败项逐行重试)。
  const watchIds = (ids: number[]) => {
    setLiveResult(null);
    setLiveSettled(0);
    setLiveEntries([]);
    setLiveError(null);
    setLiveStalled(false);
    setLiveElapsed(0);
    setLiveWatch({ ids, startedAt: Date.now() });
  };

  const retryTask = async (taskId: number) => {
    await distributionsApi.retry(taskId);
    watchIds([taskId]);
    qc.invalidateQueries({ queryKey: ["tasks"] });
  };

  const keyCountLabel =
    mode === "single" ? "1 把 Key" : mode === "multi" ? `${multiKeyLines.length} 条 Key` : `${batchKeyLines.length} 条 Key`;
  const submitLabel =
    totalSelected === 0
      ? "选择站点后分发"
      : `${keyCountLabel} → 中转站 ${selectedRelay.size} · 推送站 ${selectedPush.size}`;

  const recentTasks = tasksQ.data?.items ?? [];
  const recentTotal = tasksQ.data?.total ?? 0;
  const failedCount = tasksQ.data?.items.filter((t) => t.status === "failed" || t.status === "unknown").length ?? 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-widest text-primary">Main template / distribute</p>
          <h1 className="text-xl font-semibold">主模板 · 一键分发</h1>
          <p className="mt-0.5 text-xs text-muted-foreground">
            选一份主模板,将它的配置与 Key 一次下发到<strong className="text-foreground">绑定的中转站 + 推送站</strong>。
            链路:主协议(原厂)→ 主模板 → 分别绑定 中转站 / 推送站。
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2 text-xs text-muted-foreground">
          <span>{keyCountQ.data?.total ?? "—"} 条 Key</span>
          <span>·</span>
          <span>中转站 {enabledRelay.length}</span>
          <span>·</span>
          <span>推送站 {enabledPush.length}</span>
        </div>
      </div>

      {(msg || err) && (
        <div
          className={`rounded-md border px-3 py-2 text-sm ${
            err ? "border-destructive/40 bg-destructive/5 text-destructive" : "border-green-200 bg-green-50 text-green-800"
          }`}
        >
          {err ?? msg}
          <button type="button" onClick={clearBanner} className="ml-2 align-middle text-xs underline-offset-2 hover:underline">
            知道了
          </button>
        </div>
      )}

      {liveWatch && (() => {
        const inflight = liveEntries.filter((e) => !isTaskSettled(e.status));
        const shown = inflight.slice(0, 8);
        const total = liveWatch.ids.length;
        return (
          <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            {liveStalled ? (
              <>
                <div className="font-medium">
                  前台轮询已停止(已等待 {fmtDuration(liveElapsed)},超过 {fmtDuration(LIVE_WATCH_MAX_MS)})。
                  {total - liveSettled} 个任务仍未定局,后台会继续异步推进。
                </div>
                <div className="mt-1 flex items-center gap-3 text-xs">
                  <button
                    type="button"
                    onClick={() => watchIds(liveWatch.ids)}
                    className="inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-primary hover:bg-accent"
                  >
                    <RefreshCw className="h-3 w-3" /> 继续等待
                  </button>
                  <button type="button" onClick={clearBanner} className="underline-offset-2 hover:underline">
                    知道了(去下方任务列表查看)
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <span className="inline-flex h-3 w-3 animate-spin rounded-full border-2 border-amber-500 border-t-transparent" />
                  <span>
                    分发进行中… {liveSettled}/{total} 已完成 · 已用时 {fmtDuration(liveElapsed)},后台异步推进中,请稍候
                  </span>
                </div>
                {liveError && <div className="mt-1 text-xs text-destructive">{liveError}</div>}
                {shown.length > 0 && (
                  <ul className="mt-1 space-y-0.5">
                    {shown.map((e) => (
                      <li key={e.taskId} className="flex items-center gap-2 text-xs">
                        <Badge variant={statusVariant[e.status]}>{STATUS_LABEL[e.status]}</Badge>
                        <span className="font-medium">{e.keyName}</span>
                        <span className="text-muted-foreground">→ {e.siteName}</span>
                      </li>
                    ))}
                    {inflight.length > shown.length && (
                      <li className="text-xs text-muted-foreground">…另有 {inflight.length - shown.length} 个在途</li>
                    )}
                  </ul>
                )}
              </>
            )}
          </div>
        );
      })()}

      {liveResult && (() => {
        const success = liveResult.filter((e) => e.status === "success").length;
        const failed = liveResult.filter((e) => e.status !== "success").length;
        const hasFail = failed > 0;
        return (
          <div className={`rounded-md border px-3 py-2 text-sm ${hasFail ? "border-amber-300 bg-amber-50 text-amber-900" : "border-green-200 bg-green-50 text-green-800"}`}>
            <div className="mb-1 font-medium">
              分发定局:{liveResult.length} 个任务 · 成功 {success} 个{hasFail ? ` · 失败 ${failed} 个` : ""}
              <span className="ml-3 font-normal text-xs text-muted-foreground">
                {hasFail ? "失败项可逐行重试" : "全部完成"}
              </span>
            </div>
            {hasFail && (
              <ul className="space-y-1">
                {liveResult.filter((e) => e.status !== "success").map((e) => (
                  <li key={e.taskId} className="flex items-center gap-2 text-xs">
                    <span className="inline-flex shrink-0 items-center gap-1">
                      <Badge variant={statusVariant[e.status]}>{STATUS_LABEL[e.status]}</Badge>
                    </span>
                    <span className="font-medium">{e.keyName}</span>
                    <span className="text-muted-foreground">→ {e.siteName}</span>
                    <span className="min-w-0 flex-1 truncate text-muted-foreground" title={e.error}>
                      {e.error || `#${e.taskId}`}
                    </span>
                    <button
                      type="button"
                      onClick={() => retryTask(e.taskId)}
                      className="inline-flex shrink-0 items-center gap-1 rounded-md border px-2 py-0.5 text-primary hover:bg-accent"
                    >
                      <RefreshCw className="h-3 w-3" /> 重试
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <button type="button" onClick={clearBanner} className="mt-1 text-xs underline-offset-2 hover:underline">
              关闭
            </button>
          </div>
        );
      })()}

      {batchErrors.length > 0 && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          <div className="mb-1 font-medium">以下 {batchErrors.length} 条需要处理:</div>
          <ul className="space-y-0.5">
            {batchErrors.map((e) => (
              <li key={`${e.kind}-${e.index}`} className="text-xs">
                {e.kind === "import" ? "导入" : e.kind === "confirm" ? "待确认" : "分发"} 第 {e.index + 1} 行「{e.name || "(空名称)"}」:{e.error}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* 步骤 1:选主模板 —— 预勾选其绑定的中转站 + 推送站,并套用协议/模型/映射预设 */}
      <section className={sectionCls}>
        <header className="border-b px-5 py-3">
          <h2 className="text-sm font-semibold">① 选主模板</h2>
          <p className={`${hintCls} mt-0.5`}>选中后自动勾选该模板已绑定的中转站 + 推送站,并带出协议类型;模型与映射留空继承,可点「使用模板值」显式写入。</p>
        </header>
        <div className="flex flex-wrap items-center gap-3 px-5 py-4">
          <select
            className={`${inpCls} max-w-md font-medium`}
            value={activeTemplateId ?? ""}
            onChange={(e) => selectTemplate(e.target.value === "" ? null : Number(e.target.value))}
          >
            <option value="">— 不套用模板(手动选站点)—</option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.code} · {t.name}
              </option>
            ))}
          </select>
          <div className="flex items-center gap-2" title="协议类型随主模板自动带出,只读">
            <span className="text-xs font-medium text-muted-foreground">协议类型</span>
            <span className="inline-flex h-9 items-center rounded-md border bg-muted/40 px-3 text-sm text-muted-foreground">
              {protocolType != null
                ? `${protoName.get(protocolType) ?? "协议"} (${protocolType})`
                : "选主模板后自动带出"}
            </span>
          </div>
          {templates.length === 0 && <span className="text-xs text-muted-foreground">暂无主模板(见「主模板 → 模板」)</span>}
          {activeTemplate && (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/5 px-2.5 py-1 text-[11px] text-primary">
                template_code <code className="font-mono">{activeTemplate.code}</code>
              </span>
              <span className="inline-flex items-center gap-1 rounded-full border bg-muted px-2.5 py-1 text-[11px] text-muted-foreground">
                protocol_type <code className="font-mono">{activeTemplate.protocol_type}</code>
                {protoName.get(activeTemplate.protocol_type) ? ` · ${protoName.get(activeTemplate.protocol_type)}` : ""}
              </span>
              <span className="inline-flex items-center gap-1 rounded-full border bg-muted px-2.5 py-1 text-[11px] text-muted-foreground">
                template_id <code className="font-mono">{activeTemplate.id}</code>
              </span>
            </div>
          )}
        </div>
      </section>

      {unboundWarning && (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          ⚠ {unboundWarning}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_380px]">
        <div className="space-y-4">
          <section className={sectionCls}>
            <header className="border-b px-5 py-3">
              <h2 className="text-sm font-semibold">② 基本信息</h2>
              <p className={`${hintCls} mt-0.5`}>Key 名称、渠道模式、协议类型与优先级。</p>
            </header>
            <div className="grid grid-cols-2 gap-x-4 gap-y-3.5 px-5 py-4">
              <div className="col-span-2 space-y-1">
                <label className={labelCls}>名称 <span className="text-destructive">*</span></label>
                <input
                  className={inpCls}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={mode === "single" ? "例:prod-openai" : "例:prod-openai(留空自动生成)"}
                  name="upstream-key-display-name"
                  autoComplete="off"
                />
              </div>
              <div className="space-y-1 col-span-2">
                <label className={labelCls}>渠道模式 <span className="text-destructive">*</span></label>
                <select className={inpCls} value={mode} onChange={(e) => changeMode(e.target.value as Mode)}>
                  <option value="single">单密钥</option>
                  <option value="multi">多密钥</option>
                  <option value="batch">批量添加</option>
                </select>
                <p className={hintCls}>切换模式会重置 API Key 内容</p>
              </div>
            </div>
          </section>

          <section className={sectionCls}>
            <header className="flex items-center border-b px-5 py-3">
              <div>
                <h2 className="text-sm font-semibold">③ 认证凭证</h2>
                <p className={`${hintCls} mt-0.5`}>{KEY_SUBTITLE[mode]}</p>
              </div>
              {mode === "multi" && (
                <button
                  type="button"
                  className="ml-auto inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs text-primary hover:bg-accent"
                  onClick={() => setMultiText(multiKeyLines.join("\n"))}
                >
                  <RefreshCw className="h-3 w-3" /> 去除重复
                </button>
              )}
            </header>
            <div className="space-y-3 px-5 py-4">
              <div className="space-y-1">
                <label className={labelCls}>密钥格式 <span className="text-destructive">*</span></label>
                <select className={inpCls} value={keyFormat} onChange={(e) => setKeyFormat(e.target.value as KeyFormat)}>
                  {(Object.keys(KEY_FORMAT_LABEL) as KeyFormat[]).map((f) => (
                    <option key={f} value={f}>{KEY_FORMAT_LABEL[f]}</option>
                  ))}
                </select>
                <p className={hintCls}>
                  决定上游 Key 的呈现方式;Bearer / x-api-key 直接填密钥,SigV4 需填 <code className="rounded bg-muted px-1">AK|SK|Region</code>(管道分隔)。
                </p>
              </div>
              {mode === "single" ? (
                <div className="space-y-1">
                    <label className={labelCls}>Key(明文) <span className="text-destructive">*</span></label>
                    <input
                      ref={keyInputRef}
                      className={inpCls}
                      type="text"
                      value={keyText}
                      onChange={(e) => setKeyText(e.target.value)}
                      placeholder={KEY_FORMAT_PLACEHOLDER[keyFormat].single}
                      name="upstream-api-key"
                      autoComplete="new-password"
                      data-1p-ignore="true"
                      data-lpignore="true"
                    />
                  </div>
              ) : (
                <>
                  <label className={labelCls}>Key(明文) <span className="text-destructive">*</span></label>
                  <textarea
                    ref={mode === "multi" ? multiInputRef : batchInputRef}
                    className="min-h-[200px] w-full rounded-lg border bg-background px-2.5 py-2 font-mono text-xs outline-none focus:ring-2 focus:ring-ring"
                    value={mode === "multi" ? multiText : batchText}
                    onChange={(e) => (mode === "multi" ? setMultiText(e.target.value) : setBatchText(e.target.value))}
                    placeholder={KEY_FORMAT_PLACEHOLDER[keyFormat].multi}
                  />
                  <div className="flex items-center justify-between">
                    <span className={hintCls}>
                      已解析 <strong className="text-foreground">{mode === "multi" ? multiKeyLines.length : batchKeyLines.length}</strong> 条{mode === "multi" ? "(去重后)" : ""}
                    </span>
                  </div>
                  <p className={hintCls}>{KEY_HINT[mode]}</p>
                </>
              )}
              <div className="space-y-1">
                <label className={labelCls}>Base URL</label>
                <input className={inpCls} value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} placeholder="留空使用默认(AWS / Vertex / Azure 按实例填)" />
              </div>
            </div>
          </section>

          <section className={sectionCls}>
            <header className="border-b px-5 py-3">
              <h2 className="text-sm font-semibold">④ 模型与重定向</h2>
              <p className={`${hintCls} mt-0.5`}>下发到下游渠道的模型列表与 model_mapping 重定向规则。</p>
            </header>
            <div className="space-y-3.5 px-5 py-4">
              <div className="space-y-1">
                <label className={labelCls}>模型列表</label>
                <input className={`${inpCls} font-mono text-xs`} value={models} onChange={(e) => setModels(e.target.value)} placeholder="gpt-4o,gpt-4o-mini,glm-4-flash" />
                <p className={hintCls}>逗号分隔;下游据此路由。留空 = 继承绑定/站点/模板。model_mapping 的「源」模型必须在此列表内。</p>
                {activeTemplate && (
                  <div className="flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
                    <span>
                      模板默认：<code className="font-mono">{activeTemplate.models || "空"}</code>
                      {activeTemplate.model_mapping ? " · 含 mapping" : " · 无映射"}
                    </span>
                    {(activeTemplate.models || activeTemplate.model_mapping) && (
                      <button
                        type="button"
                        className="rounded border px-1.5 py-0.5 text-primary hover:bg-accent"
                        onClick={useTemplateDefaults}
                      >
                        使用模板值
                      </button>
                    )}
                  </div>
                )}
              </div>
              <div className="space-y-1">
                <label className={labelCls}>模型重定向 model_mapping</label>
                <ModelMappingEditor
                  value={modelMapping}
                  onChange={setModelMapping}
                  sourceModelOptions={modelOptions}
                  targetModelOptions={modelOptions}
                />
              </div>
            </div>
          </section>

          <section className={sectionCls}>
            <header className="flex cursor-pointer items-center border-b px-5 py-3" onClick={() => setAdvOpen((v) => !v)}>
              <div>
                <h2 className="text-sm font-semibold">高级设置</h2>
                <p className={`${hintCls} mt-0.5`}>供应商与批发单价。</p>
              </div>
              {advOpen ? <ChevronUp className="ml-auto h-4 w-4 text-muted-foreground" /> : <ChevronDown className="ml-auto h-4 w-4 text-muted-foreground" />}
            </header>
            {advOpen && (
              <div className="grid grid-cols-2 gap-x-4 gap-y-3.5 px-5 py-4">
                <div className="space-y-1">
                  <label className={labelCls}>供应商</label>
                  <input className={inpCls} value={supplierField} onChange={(e) => setSupplierField(e.target.value)} placeholder="采购来源(可选)" />
                </div>
                <div className="space-y-1">
                  <label className={labelCls}>批发单价</label>
                  <input className={inpCls} type="number" step="0.0001" value={unitPrice} onChange={(e) => setUnitPrice(e.target.value)} placeholder="0.0000" />
                  <p className={hintCls}>元/quota,用于对账。</p>
                </div>
              </div>
            )}
          </section>
        </div>

        <aside className="space-y-4">
          <div className="sticky top-4 space-y-4">
            <section className={sectionCls}>
              <header className="border-b px-4 py-3">
                <div className="flex items-center gap-2">
                  <h2 className="text-sm font-semibold">⑤ 选择下发目标</h2>
                  <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">已选 {totalSelected}</span>
                </div>
                <div className="mt-1.5 flex gap-2 text-[11px] text-muted-foreground">
                  <span className="rounded-full border bg-background px-2 py-0.5">中转站 <strong className="font-mono text-foreground">{selectedRelay.size}</strong>/{relayPool.length}</span>
                  <span className="rounded-full border bg-background px-2 py-0.5">推送站 <strong className="font-mono text-foreground">{selectedPush.size}</strong>/{pushPool.length}</span>
                </div>
                <p className="mt-1.5 text-[10px] leading-4 text-muted-foreground">
                  模型优先级：<strong className="text-foreground">绑定 &gt; 站点 &gt; 分发 &gt; 模板</strong>；留空 = 继承。悬停/focus 目标查看参数与来源。
                </p>
              </header>

              {/* 已绑定中转站 */}
              <div className="border-b px-4 py-3">
                <div className="mb-2 flex items-center gap-2">
                  <Globe className="h-3.5 w-3.5 text-primary" />
                  <span className="text-xs font-semibold">中转站 <span className="font-normal text-muted-foreground">site_type=relay</span></span>
                  <div className="ml-auto flex items-center gap-2 text-[11px]">
                    <button type="button" className="text-primary hover:underline" onClick={() => selectAllRelay(true)}>全选</button>
                    <span className="text-muted-foreground">·</span>
                    <button type="button" className="text-primary hover:underline" onClick={() => selectAllRelay(false)}>清空</button>
                  </div>
                </div>
                <div className="relative mb-2">
                  <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                  <input
                    className="h-8 w-full rounded-md border bg-background pl-7 pr-2 text-xs outline-none focus:ring-2 focus:ring-ring"
                    value={relaySearch}
                    onChange={(e) => setRelaySearch(e.target.value)}
                    placeholder="筛选中转站…"
                  />
                </div>
                <div className="max-h-[26vh] space-y-1.5 overflow-y-auto pr-1">
                  {filteredRelay.length === 0 && (
                    <div className="py-6 text-center text-xs text-muted-foreground">
                      {relayPool.length === 0
                        ? (activeTemplateId != null ? "该主模板未绑定中转站" : "暂无启用的中转站")
                        : "无匹配"}
                    </div>
                  )}
                  {filteredRelay.map((s) => {
                    const checked = selectedRelay.has(s.code);
                    return (
                      <label
                        key={s.code}
                        tabIndex={0}
                        onMouseEnter={(e) => showTargetHint("relay", s, e.currentTarget)}
                        onMouseLeave={scheduleHideTargetHint}
                        onFocus={(e) => showTargetHint("relay", s, e.currentTarget)}
                        onBlur={(e) => {
                          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) hideTargetHint();
                        }}
                        className={`flex cursor-pointer items-center gap-2 rounded-lg border px-2.5 py-2 text-xs transition-colors ${
                          checked ? "border-primary bg-primary/5" : "hover:border-primary"
                        }`}
                      >
                        <input type="checkbox" className="accent-primary" checked={checked} onChange={() => toggleRelay(s.code)} />
                        <div className="min-w-0 flex-1">
                          <div className="truncate font-medium">{s.name} <span className="font-mono text-[10px] text-muted-foreground">{s.code}</span></div>
                          <div className="truncate text-[10px] text-muted-foreground">{s.url}</div>
                        </div>
                        <span className="shrink-0 rounded-full bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">{s.adapter_type}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* 已绑定推送站 */}
              <div className="px-4 py-3">
                <div className="mb-2 flex items-center gap-2">
                  <Rocket className="h-3.5 w-3.5 text-violet-600" />
                  <span className="text-xs font-semibold">推送站 <span className="font-normal text-muted-foreground">site_type=push</span></span>
                  <div className="ml-auto flex items-center gap-2 text-[11px]">
                    <button type="button" className="text-primary hover:underline" onClick={() => selectAllPush(true)}>全选</button>
                    <span className="text-muted-foreground">·</span>
                    <button type="button" className="text-primary hover:underline" onClick={() => selectAllPush(false)}>清空</button>
                  </div>
                </div>
                <div className="relative mb-2">
                  <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                  <input
                    className="h-8 w-full rounded-md border bg-background pl-7 pr-2 text-xs outline-none focus:ring-2 focus:ring-ring"
                    value={pushSearch}
                    onChange={(e) => setPushSearch(e.target.value)}
                    placeholder="筛选推送站…"
                  />
                </div>
                <div className="max-h-[26vh] space-y-1.5 overflow-y-auto pr-1">
                  {filteredPush.length === 0 && (
                    <div className="py-6 text-center text-xs text-muted-foreground">
                      {pushPool.length === 0
                        ? (activeTemplateId != null ? "该主模板未绑定推送站" : "暂无启用的推送站")
                        : "无匹配"}
                    </div>
                  )}
                  {filteredPush.map((s) => {
                    const checked = selectedPush.has(s.code);
                    const missField = pushSiteMissingKey(s);
                    return (
                      <label
                        key={s.code}
                        tabIndex={0}
                        onMouseEnter={(e) => showTargetHint("push", s, e.currentTarget)}
                        onMouseLeave={scheduleHideTargetHint}
                        onFocus={(e) => showTargetHint("push", s, e.currentTarget)}
                        onBlur={(e) => {
                          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) hideTargetHint();
                        }}
                        className={`flex cursor-pointer items-center gap-2 rounded-lg border px-2.5 py-2 text-xs transition-colors ${
                          checked ? "border-violet-500 bg-violet-50" : "hover:border-violet-400"
                        }`}
                      >
                        <input type="checkbox" className="accent-violet-600" checked={checked} onChange={() => togglePush(s.code)} />
                        <div className="min-w-0 flex-1">
                          <div className="truncate font-medium">{s.name} <span className="font-mono text-[10px] text-muted-foreground">{s.code}</span></div>
                          <div className="truncate text-[10px] text-muted-foreground">{s.url}</div>
                          {missField && (
                            <div className="text-[10px] text-amber-600" title={`该站的绑定行 field_mapping 缺 ${missField},该商家创建渠道会 pre-flight 失败(未发送:${missField} is required);请到「推送绑定」为该站配置必填字段`}>
                              ⚠ 绑定 field_mapping 缺 {missField}
                            </div>
                          )}
                        </div>
                        <span className="shrink-0 rounded-full bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">{s.adapter_type}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            </section>

            <section className={`${sectionCls} px-4 py-4`}>
              <div className="mb-3 flex items-baseline justify-between text-xs text-muted-foreground">
                <span>{totalSelected === 0 ? "未选择站点" : `${keyCountLabel} → ${totalSelected} 个站点`}</span>
                <span className="inline-flex items-center gap-1">
                  <kbd className="rounded border bg-muted px-1 text-[10px]">⌘</kbd>
                  <kbd className="rounded border bg-muted px-1 text-[10px]">↵</kbd> 提交
                </span>
              </div>
              {aggregateSelected && (
                <p className="mb-2 text-[11px] text-violet-700">
                  所选推送站含聚合上传(upload_mode=aggregate):
                  {mode === "single" ? "单 Key 直接创建一个聚合渠道。" : "整批会先暂存,全部 Key 创建完成后自动 Commit,再把整组聚合成一个下游渠道。"}
                </p>
              )}
              <button
                type="button"
                disabled={!canSubmit}
                onClick={() => submitMut.mutate()}
                className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Send className="h-4 w-4" />
                {submitMut.isPending ? "提交中…" : submitLabel}
              </button>
              {(mode === "single") && !mappingUsable && (
                <p className="mt-2 text-xs text-destructive">model_mapping JSON 无效,请检查。</p>
              )}
              {!sigv4Ok && (
                <p className="mt-2 text-xs text-destructive">SigV4 密钥须为 <code className="rounded bg-muted px-1">AK|SK|Region</code>(每行管道分隔三段)。</p>
              )}
              <button
                type="button"
                onClick={resetForm}
                className="mt-2 inline-flex w-full items-center justify-center gap-1 rounded-lg border bg-background px-4 py-1.5 text-xs hover:bg-accent"
              >
                <X className="h-3.5 w-3.5" /> 清空表单
              </button>
            </section>
          </div>
        </aside>
      </div>

      <section className={sectionCls}>
        <header className="flex items-center gap-3 border-b px-5 py-3">
          <h2 className="text-sm font-semibold">最近分发记录</h2>
          <span className="text-xs text-muted-foreground">
            本次 {recentTotal} 条
            {recentTotal > recentTasks.length ? ` · 仅展示最新 ${recentTasks.length} 条` : ""}
            {failedCount > 0 ? ` · ${recentTotal > recentTasks.length ? "预览" : ""}失败 ${failedCount} 条` : ""}
          </span>
          <button
            type="button"
            onClick={() => tasksQ.refetch()}
            className="ml-auto inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium hover:bg-accent"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${tasksQ.isFetching ? "animate-spin" : ""}`} />
            手动刷新
          </button>
          <Link to="/relay/tasks/list" className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
            中转记录 <ArrowRight className="h-3.5 w-3.5" />
          </Link>
          <Link to="/push/tasks/list" className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
            推送记录 <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </header>
        <div className="max-h-[320px] overflow-y-auto">
          {tasksQ.isLoading && <div className="px-5 py-8 text-center text-xs text-muted-foreground">加载中…</div>}
          {!tasksQ.isLoading && recentTasks.length === 0 && (
            <div className="px-5 py-12 text-center text-xs text-muted-foreground">暂无分发记录</div>
          )}
          {recentTasks.length > 0 && (
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-muted/50 text-muted-foreground">
                <tr className="text-left">
                  <th className="px-5 py-2 font-medium">Key</th>
                  <th className="px-2 py-2 font-medium">目标站点</th>
                  <th className="px-2 py-2 font-medium">类型</th>
                  <th className="px-2 py-2 font-medium">状态</th>
                  <th className="px-2 py-2 font-medium">模板</th>
                  <th className="px-2 py-2 font-medium">时间</th>
                  <th className="px-5 py-2 font-medium">错误</th>
                </tr>
              </thead>
              <tbody>
                {recentTasks.map((t: DistributionTask) => (
                  <tr key={t.id} className="border-b hover:bg-muted/30">
                    <td className="px-5 py-2">
                      <div className="font-medium">{keyById.get(t.key_id) ?? `#${t.key_id}`}</div>
                      <div className="text-[10px] text-muted-foreground">#{t.id}</div>
                    </td>
                    <td className="px-2 py-2 text-muted-foreground">{siteLabel(t)}</td>
                    <td className="px-2 py-2">
                      <Badge variant={t.site_type === "push" ? "warning" : "default"}>{t.site_type === "push" ? "推送" : "中转"}</Badge>
                    </td>
                    <td className="px-2 py-2"><Badge variant={statusVariant[t.status]}>{STATUS_LABEL[t.status]}</Badge></td>
                    <td className="px-2 py-2 font-mono text-[10px] text-muted-foreground">{t.template_snapshot || "—"}</td>
                    <td className="px-2 py-2 font-mono text-[11px] text-muted-foreground">{relativeTime(t.dispatched_at ?? t.created_at)}</td>
                    <td className="px-5 py-2">
                      {t.error_message ? (
                        <span className="inline-block max-w-xs truncate text-destructive" title={t.error_message}>{t.error_message}</span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      <TargetConfigHint
        data={targetHint}
        anchor={hintTarget?.rect ?? null}
        onMouseEnter={cancelHideTargetHint}
        onMouseLeave={hideTargetHint}
      />
    </div>
  );
}
